import type React from 'react'
import { Button, Divider, Space, Tooltip } from 'antd'
import { fetchWithAuth, toSec } from '../../fn'

// Palette comandi della flotta SIM, condivisa da Connections e dalla schermata SMS.
// Tenerla in un solo posto evita che le due schermate divergano (era già successo con altri fork).

interface Command {
  key: string
  icon: string
  title: string
  command: string
  description: string
}

interface AdbCommand {
  label: string
  action: string
  endpoint: 'command' | 'deploy'
}

interface NotificationArgs {
  type: 'success' | 'error'
  message: string
  description: string
}

export interface PhoneCommandsProps {
  record: { id?: string | number, adbLastSeen?: number }
  apiUrl: string
  open?: (args: NotificationArgs) => void
  onAfterCommand?: (key: string) => void
  showLabels?: boolean
}

const USSD_COMMANDS: Command[] = [
  { key: 'credit', icon: '💰', title: 'Get Credit', command: 'abracadabra-ussd-*131#', description: 'Inviata richiesta di credito' },
  { key: 'data', icon: '🌐', title: 'Get Data', command: 'abracadabra-ussd-*137#', description: 'Inviata richiesta dati' },
  { key: 'number', icon: '🔢', title: 'Get Number', command: 'abracadabra-ussd-*132#', description: 'In attesa della risposta della SIM (≈10s)' },
  { key: 'wifi', icon: '🛜', title: 'Wifi', command: 'abracadabra-wifi', description: 'Inviata richiesta di abilitazione wifi' },
  { key: 'nowifi', icon: '📶', title: '5G', command: 'abracadabra-nowifi', description: 'Inviata richiesta di disabilitare wifi' },
  { key: 'newip', icon: '🆕', title: 'Ottieni Nuovo IP', command: 'abracadabra-newip', description: 'Inviata richiesta di nuovo ip' }
]

const SYSTEM_COMMANDS: Command[] = [
  { key: 'volume', icon: '🔊', title: 'Set volume 0', command: 'abracadabra-volume-0', description: 'Inviata comando per volume 0' },
  { key: 'wakeup', icon: '⏰', title: 'Wake up', command: 'abracadabra-wakeup', description: 'Inviata richiesta wakeup' },
  { key: 'clear', icon: '🆑', title: 'Clear Log', command: 'abracadabra-clear', description: 'Inviata richiesta di cancellazione log' },
  { key: 'init', icon: '☠️', title: 'Init', command: 'abracadabra-init', description: 'Inviata richiesta di inizializzazione' },
  { key: 'upgrade', icon: '🆙', title: 'Upgrade', command: 'abracadabra-upgrade', description: 'Inviata richiesta di upgrade' }
]

const ADB_COMMANDS: AdbCommand[] = [
  { label: 'WiFi On', action: 'wifi-on', endpoint: 'command' },
  { label: 'WiFi Off', action: 'wifi-off', endpoint: 'command' },
  { label: 'Tether On', action: 'tethering-on', endpoint: 'command' },
  { label: 'Tether Off', action: 'tethering-off', endpoint: 'command' },
  { label: 'Restart', action: 'restart-agent', endpoint: 'command' },
  { label: 'Deploy', action: 'deploy', endpoint: 'deploy' }
]

const ADB_FRESH_S = 120

export const PhoneCommands: React.FC<PhoneCommandsProps> = ({ record, apiUrl, open, onAfterCommand, showLabels = true }): React.JSX.Element => {
  const id = record?.id ?? ''
  const adbLastSeen = record?.adbLastSeen ?? 0
  const adbFresh = Boolean(adbLastSeen) && toSec(adbLastSeen) <= ADB_FRESH_S
  const adbReason = !adbLastSeen ? 'ADB non rilevato su questo telefono' : `ADB non visto da ${toSec(adbLastSeen)}s`

  const send = (item: Command): void => {
    fetchWithAuth(`${apiUrl}/phone/registerCommand`, { method: 'POST', body: { instance: `automate-${String(id)}`, command: item.command } })
      .then((a) => {
        console.log(a)
        open?.({ type: 'success', message: `Richiesta inviata al telefono ${String(id)}`, description: item.description })
        onAfterCommand?.(item.key)
      })
      .catch(() => {
        open?.({ type: 'error', message: 'Errore', description: `Errore nella richiesta: ${item.title}` })
      })
  }

  const sendAdb = (adb: AdbCommand): void => {
    const url = adb.endpoint === 'deploy'
      ? `${apiUrl}/adb/deploy/${String(id)}`
      : `${apiUrl}/adb/command/${String(id)}/${adb.action}`
    fetchWithAuth(url, { method: 'POST' })
      .then((a) => {
        console.log(a)
        open?.({ type: 'success', message: `ADB command sent to ${String(id)}`, description: adb.label })
        onAfterCommand?.(`adb-${adb.action}`)
      })
      .catch(() => {
        open?.({ type: 'error', message: 'Error', description: `Failed to send ${adb.label} command` })
      })
  }

  return (
    <>
      {showLabels && <div style={{ fontSize: '10px', color: '#888', marginBottom: '2px' }}>Automate (backup)</div>}
      <Space wrap>
        {USSD_COMMANDS.map((item) => (
          <Button key={item.key} title={`${item.icon} ${item.title}`} size='small' onClick={() => { send(item) }}>
            {item.icon}
          </Button>
        ))}
      </Space>

      <Space wrap style={{ marginTop: '5px' }}>
        {SYSTEM_COMMANDS.map((item) => (
          <Button key={item.key} title={`${item.icon} ${item.title}`} size='small' onClick={() => { send(item) }}>
            {item.icon}
          </Button>
        ))}
      </Space>

      <Divider style={{ margin: '6px 0', fontSize: '11px' }} orientation='left' plain>ADB / Termux</Divider>

      <Space wrap style={{ marginTop: '2px' }}>
        {ADB_COMMANDS.map((adb) => (
          <Tooltip key={adb.action} title={adbFresh ? adb.label : adbReason}>
            {/* il Button disabilitato non emette eventi: lo span serve a far funzionare il tooltip */}
            <span>
              <Button size='small' disabled={!adbFresh} onClick={() => { sendAdb(adb) }}>
                {adb.label}
              </Button>
            </span>
          </Tooltip>
        ))}
      </Space>
    </>
  )
}
